import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, GhostButton } from "../components/Primitives";
import PortfolioRollups from "../components/PortfolioRollups";
import PortalRecentIncidents from "../components/PortalRecentIncidents";
import MetricStatusPill from "../components/MetricStatusPill";
import Sparkline from "../components/Sparkline";
import TrendDelta from "../components/TrendDelta";
import {
  Building2, Clock, Route as RouteIcon, ClipboardCheck,
  AlertOctagon, Wrench, UserCheck, Radio, Activity, ArrowRight,
  X as XIcon, Info, CheckCheck, Timer, ShieldCheck, MapPin, FileCheck, MessageSquareWarning,
} from "lucide-react";

/**
 * Static presentation metadata per metric key.
 *   render(v)  → the big number
 *   suffix(v)  → the muted subtitle
 *
 * NOTE: label + tooltip come from the per-client config now; we only fall
 * back to `defaultLabel` if the config row is missing (very old data).
 */
const METRIC_META = {
  sites:             { icon: Building2,      accent: "#0A84FF", defaultLabel: "Sites under coverage",   render: (v) => (v?.active ?? 0),                                                    suffix: (v) => `of ${v?.total ?? 0} sites` },
  guard_hours:       { icon: Clock,          accent: "#22C55E", defaultLabel: "Officer-hours delivered",  render: (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }), suffix: () => "hours" },
  tour_scans:        { icon: RouteIcon,      accent: "#0A84FF", defaultLabel: "Checkpoint scans",       render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "checkpoint hits" },
  dars:              { icon: ClipboardCheck, accent: "#FFD60A", defaultLabel: "Daily activity reports", render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "DARs filed" },
  incidents:         { icon: AlertOctagon,   accent: "#FF3B30", defaultLabel: "Incidents reported",     render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "reports" },
  maintenance:       { icon: Wrench,         accent: "#FF9F0A", defaultLabel: "Maintenance issues",     render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "requests raised" },
  attendance:        { icon: UserCheck,      accent: "#22C55E", defaultLabel: "On-time attendance",     render: (v) => (v?.on_time_pct == null ? "—" : `${v.on_time_pct}%`),                 suffix: (v) => (v?.total_shifts ? `over ${v.total_shifts} shifts` : "no shifts") },
  live_coverage:     { icon: Radio,          accent: "#22C55E", defaultLabel: "Live coverage right now",render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "officers on duty" },
  activity_total:    { icon: Activity,       accent: "#FF3B30", defaultLabel: "Total activity events",  render: (v) => Number(v || 0).toLocaleString(),                                     suffix: () => "events for you" },
  resolution_rate:   { icon: CheckCheck,     accent: "#22C55E", defaultLabel: "Incident resolution",    render: (v) => (v?.pct == null ? "—" : `${v.pct}%`),                                 suffix: (v) => (v?.total ? `${v.resolved}/${v.total} resolved` : "no incidents") },
  response_time:     { icon: Timer,          accent: "#0A84FF", defaultLabel: "Avg. response time",     render: (v) => (v?.median_min == null ? "—" : `${v.median_min}m`),                   suffix: (v) => (v?.sample ? `median · ${v.sample} events` : "not enough data") },
  patrol_coverage:      { icon: ShieldCheck, accent: "#22C55E", defaultLabel: "Patrol coverage rate",   render: (v) => (v?.pct == null ? "—" : `${v.pct}%`),                                 suffix: (v) => (v?.total ? `${v.completed}/${v.total} shifts` : "no shifts") },
  checkpoint_compliance:{ icon: MapPin,      accent: "#0A84FF", defaultLabel: "Checkpoint on-time %",   render: (v) => (v?.pct == null ? "—" : `${v.pct}%`),                                 suffix: (v) => (v?.expected ? `${v.actual}/${v.expected} scans` : "no stops defined") },
  report_timeliness:    { icon: FileCheck,   accent: "#22C55E", defaultLabel: "Report timeliness",      render: (v) => (v?.pct == null ? "—" : `${v.pct}%`),                                 suffix: (v) => (v?.total ? `${v.on_time}/${v.total} on-time` : "no shifts") },
  complaints:           { icon: MessageSquareWarning, accent: "#EF4444", defaultLabel: "Client complaints", render: (v) => Number(v || 0).toLocaleString(),                                  suffix: (v) => (v ? "complaints logged" : "no complaints") },
  supervisor_visits:    { icon: UserCheck,   accent: "#22C55E", defaultLabel: "Supervisor site visits", render: (v) => Number(v || 0).toLocaleString(),                                  suffix: (v) => (v ? "visits logged" : "no visits") },
};

const WINDOW_OPTIONS = [
  { days: 7,  label: "1 week" },
  { days: 30, label: "1 month" },
  { days: 90, label: "3 months" },
];


export default function PortalDashboard() {
  const nav = useNavigate();
  const [days, setDays]         = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [data, setData]         = useState(null);
  const [config, setConfig]     = useState(null);
  const [trends, setTrends]     = useState(null);
  const [rollups, setRollups]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate && endDate) {
        params.start = new Date(startDate + "T00:00:00").toISOString();
        params.end   = new Date(endDate   + "T23:59:59").toISOString();
      } else {
        params.days = days;
      }
      // Trends only make sense on rolling-day windows (not custom start/end),
      // so skip the trends fetch if the user has picked a custom range.
      const trendsParams = (startDate && endDate) ? null : { days };
      const [r, cfg, tr, ro] = await Promise.all([
        api.get("/portal/dashboard-metrics", { params }),
        api.get("/portal/config").catch(() => ({ data: null })),
        trendsParams
          ? api.get("/portal/dashboard-trends", { params: trendsParams }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
        api.get("/portal/portfolio-rollups", { params }).catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setConfig(cfg?.data || null);
      setTrends(tr?.data || null);
      setRollups(ro?.data || null);
    } catch (err) { setData(null); setTrends(null); setRollups(null); }
    finally { setLoading(false); }
  }, [days, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const hasCustomRange = Boolean(startDate && endDate);
  const clearCustomRange = () => { setStartDate(""); setEndDate(""); };
  const onPresetClick = (d) => { setDays(d); clearCustomRange(); };

  // Stable memos — `data?.foo || {}` returns a fresh empty object every
  // render when data isn't loaded yet, which retriggers every downstream
  // useMemo. Wrapping keeps the identity stable.
  const enabled = useMemo(() => data?.enabled  || {}, [data]);
  const metrics = useMemo(() => data?.metrics  || {}, [data]);
  const status  = data?.status   || {};
  // iter245 — Health score removed from the client-facing dashboard.
  // Any composite health signal stays internal (admin/ops only).
  const sitesInScope = data?.sites_in_scope ?? 0;

  // Order + section labels + custom labels come from the client's config.
  // If no config was returned, fall back to the legacy alphabetical order.
  const sections = useMemo(() => config?.sections || [], [config]);
  const metricRows = useMemo(() => {
    const rows = (config?.metrics || []).filter((m) => m.enabled && metrics[m.key] !== undefined);
    if (rows.length) return rows;
    // Legacy fallback — synthesize rows from the `enabled` map.
    return Object.keys(METRIC_META)
      .filter((k) => enabled[k])
      .map((k, i) => ({
        key: k, enabled: true, order: i,
        label: METRIC_META[k].defaultLabel, tooltip: "",
        section: "quality", thresholds: {},
      }));
  }, [config, metrics, enabled]);

  const groupedRows = useMemo(() => {
    const byKey = new Map();
    for (const s of (sections.length ? sections : [{ key: "quality", label: "Metrics", order: 0 }])) {
      byKey.set(s.key, { section: s, rows: [] });
    }
    for (const m of metricRows) {
      const secKey = m.section || "quality";
      if (!byKey.has(secKey)) byKey.set(secKey, { section: { key: secKey, label: secKey, order: 999 }, rows: [] });
      byKey.get(secKey).rows.push(m);
    }
    return Array.from(byKey.values())
      .filter((g) => g.rows.length > 0)
      .sort((a, b) => (a.section.order || 0) - (b.section.order || 0));
  }, [metricRows, sections]);

  const anyVisible = metricRows.length > 0;

  const rangeLabel = hasCustomRange
    ? `${startDate} → ${endDate}`
    : `Last ${days} ${days === 1 ? "day" : "days"}`;

  return (
    <div data-testid="portal-dashboard-page">
      <PageHeader
        title="Working For You"
        subtitle="A rolling snapshot of the security activity our officers have delivered across your sites. We observe. We report. You stay informed."
        action={
          <GhostButton data-testid="portal-goto-reports" onClick={() => nav("/portal/reports/incidents")}>
            Open Reports <ArrowRight className="size-4 ml-1" />
          </GhostButton>
        }
      />

      <div className="p-8 space-y-10">
        {/* Time-window selector */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mute">Rolling window</div>
            <div className="font-display text-lg uppercase font-bold tracking-tight mt-1">
              {rangeLabel}
              {sitesInScope > 0 && (
                <span className="text-zinc-500 font-mono text-xs ml-3">
                  · {sitesInScope} {sitesInScope === 1 ? "site" : "sites"} in scope
                </span>
              )}
            </div>
          </div>
          <div className="flex items-end gap-3 flex-wrap" data-testid="portal-window-switcher">
            <div className="inline-flex items-center gap-2">
              {WINDOW_OPTIONS.map((opt) => {
                const active = !hasCustomRange && days === opt.days;
                return (
                  <button
                    key={opt.days}
                    data-testid={`portal-window-${opt.days}d`}
                    onClick={() => onPresetClick(opt.days)}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors duration-150 ${
                      active
                        ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
                        : "border-[#27272A] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="inline-flex items-center gap-2">
              <input
                type="date"
                data-testid="portal-dashboard-start-date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-zinc-200 text-xs px-2 py-1.5 outline-none focus:border-[#FF3B30] font-mono"
              />
              <span className="text-zinc-500 text-xs">→</span>
              <input
                type="date"
                data-testid="portal-dashboard-end-date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-zinc-200 text-xs px-2 py-1.5 outline-none focus:border-[#FF3B30] font-mono"
              />
              {hasCustomRange && (
                <button
                  type="button"
                  onClick={clearCustomRange}
                  data-testid="portal-dashboard-clear-range"
                  title="Clear custom range"
                  className="p-1 text-zinc-500 hover:text-[#FF3B30]"
                >
                  <XIcon className="size-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-16 text-center text-zinc-500">Loading…</div>
        ) : !anyVisible ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-12 text-center">
            <div className="label-mute mb-2">No dashboard metrics enabled</div>
            <div className="text-zinc-400 text-sm">Your security provider hasn&apos;t enabled any dashboard metrics yet. Please contact them or open the Reports tab to view detailed activity.</div>
          </div>
        ) : (
          <>
            {/* iter245 — Health Score card removed from the client portal.
                Composite health remains an internal admin-only signal. */}

            {/* Portfolio-level rollups — shown whenever the client has 2+
                sites (single or multiple portfolios). Gives multi-property
                clients an at-a-glance summary keyed by portfolio group. */}
            {rollups?.portfolios && rollups.portfolios.length >= 1 && (rollups.sites_in_scope || 0) >= 2 && (
              <section aria-labelledby="portal-portfolios-heading" className="space-y-4">
                <div>
                  <h2 id="portal-portfolios-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
                    Portfolio Rollups
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">
                    Aggregated performance across each of your portfolios in the current window.
                  </p>
                </div>
                <PortfolioRollups portfolios={rollups.portfolios} />
              </section>
            )}

            {/* Grouped metric grid */}
            {groupedRows.map(({ section, rows }) => (
              <section
                key={section.key}
                className="space-y-4"
                aria-labelledby={`portal-section-heading-${section.key}`}
                data-testid={`portal-section-${section.key}`}
              >
                <div>
                  <h2
                    id={`portal-section-heading-${section.key}`}
                    className="font-display uppercase font-bold text-2xl tracking-tight"
                  >
                    {section.label}
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">
                    {rows.length} metric{rows.length === 1 ? "" : "s"} · {rangeLabel.toLowerCase()}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`portal-section-grid-${section.key}`}>
                  {rows.map((m) => {
                    const meta = METRIC_META[m.key] || {
                      icon: Activity, accent: "#6B7280",
                      defaultLabel: m.key, render: (v) => String(v ?? "—"), suffix: () => "",
                    };
                    const Icon = meta.icon;
                    const value = metrics[m.key];
                    const st = status[m.key] || "unknown";
                    const trend = (trends?.series || {})[m.key] || null;
                    const direction = m.thresholds?.direction || "higher_better";
                    return (
                      <div
                        key={m.key}
                        data-testid={`portal-metric-${m.key}`}
                        className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors duration-150 flex flex-col"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="label-mute flex items-center gap-1.5">
                              <span className="truncate" title={m.label}>{m.label || meta.defaultLabel}</span>
                              {m.tooltip && (
                                <span title={m.tooltip} className="cursor-help">
                                  <Info className="size-3 text-zinc-600 hover:text-zinc-400" />
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <MetricStatusPill status={st} testId={`portal-metric-status-${m.key}`} />
                              {trend && (
                                <TrendDelta
                                  delta={trend.delta_pct}
                                  direction={direction}
                                  testId={`portal-metric-delta-${m.key}`}
                                />
                              )}
                            </div>
                          </div>
                          <Icon className="size-4 flex-shrink-0" style={{ color: meta.accent }} strokeWidth={2} />
                        </div>
                        <div className="flex items-baseline gap-2 mt-4">
                          <span className="font-mono font-black text-4xl tracking-tighter" style={{ color: meta.accent }}>
                            {meta.render(value)}
                          </span>
                          <span className="text-zinc-500 text-xs uppercase tracking-widest">
                            {meta.suffix(value)}
                          </span>
                        </div>
                        {trend && trend.values && trend.values.length > 1 && (
                          <div className="mt-3 pt-3 border-t border-[#1F1F1F]">
                            <Sparkline
                              values={trend.values}
                              color={meta.accent}
                              median={trend.portfolio_median}
                              testId={`portal-metric-sparkline-${m.key}`}
                            />
                            <div className="flex items-center justify-between mt-1.5 text-[9px] uppercase tracking-widest text-zinc-500">
                              <span>{hasCustomRange ? "custom range" : `last ${days}d`}</span>
                              {trend.portfolio_median != null && trend.portfolio_sample > 0 && (
                                <span title={`Median across ${trend.portfolio_sample} client${trend.portfolio_sample === 1 ? "" : "s"} in your provider's portfolio`}>
                                  peer median · {trend.portfolio_median}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Recent Incidents feed — auto-hides if agency admin has disabled
                the `incidents` metric in the portal config so clients don't
                see a section their provider has explicitly turned off. */}
            {enabled.incidents && (
              <section aria-labelledby="portal-incidents-heading" className="space-y-4">
                <div>
                  <h2 id="portal-incidents-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
                    Recent Incidents
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">
                    Latest 10 incidents at your sites in the current window.
                  </p>
                </div>
                <PortalRecentIncidents
                  days={days}
                  startDate={hasCustomRange ? startDate : ""}
                  endDate={hasCustomRange ? endDate : ""}
                />
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
